import { cn } from '@/lib/utils/cn';

/**
 * UnitSquares — one small square per thing counted (the engagement
 * Overview, 5 Nov 2026): a session held, scheduled or not yet scheduled;
 * a vendor's rating in each client area. A population read at a glance,
 * with the count in the legend beside it. The squares wrap, so the long
 * case (60 interviews, 20 areas) stays on the card.
 *
 * Tones come from the posture and amber tokens; `empty` is an outlined
 * square (nothing yet), `pending` a dashed one (not confirmed yet). The
 * caller names the whole row for screen readers; each square carries its
 * own title for the pointer.
 */

export type UnitTone =
  | 'healthy'
  | 'amber'
  | 'at-risk'
  | 'critical'
  | 'unassessed'
  | 'empty'
  | 'pending';

export interface Unit {
  key: string;
  tone: UnitTone;
  /** One character inside the square, e.g. the rating's initial. */
  glyph?: string;
  title?: string;
}

const TONE: Record<UnitTone, string> = {
  healthy: 'bg-posture-healthy text-paper-0',
  amber: 'bg-accent-amber-600 text-paper-0',
  'at-risk': 'bg-posture-at-risk text-paper-0',
  critical: 'bg-posture-critical text-paper-0',
  unassessed: 'bg-posture-unassessed text-paper-0',
  empty: 'border-[1.5px] border-ink-200 bg-paper-0',
  pending: 'border-[1.5px] border-dashed border-ink-300 bg-paper-0',
};

/** The legend swatch of each tone, for `ChartLegend` items. */
export const UNIT_SWATCH: Record<UnitTone, string> = {
  healthy: 'bg-posture-healthy',
  amber: 'bg-accent-amber-600',
  'at-risk': 'bg-posture-at-risk',
  critical: 'bg-posture-critical',
  unassessed: 'bg-posture-unassessed',
  empty: 'border border-ink-200 bg-paper-0',
  pending: 'border border-dashed border-ink-300 bg-paper-0',
};

/** `count` squares of one tone, keyed `prefix-0`, `prefix-1`, ... */
export function unitRun(prefix: string, count: number, tone: UnitTone, title?: string): Unit[] {
  return Array.from({ length: Math.max(0, count) }, (_, i) => ({
    key: `${prefix}-${i}`,
    tone,
    ...(title ? { title } : {}),
  }));
}

export function UnitSquares({
  units,
  label,
  className,
}: {
  units: readonly Unit[];
  /** The row's accessible name, e.g. "12 held, 1 scheduled, 10 not scheduled". */
  label: string;
  className?: string;
}) {
  return (
    <div role="img" aria-label={label} className={cn('flex flex-wrap gap-[3px]', className)}>
      {units.map((u) => (
        <span
          key={u.key}
          title={u.title}
          className={cn(
            'inline-flex h-4 w-4 flex-none items-center justify-center rounded-[3px] text-[9px] font-bold leading-none',
            TONE[u.tone],
          )}
        >
          {u.glyph ?? null}
        </span>
      ))}
    </div>
  );
}
